import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";

interface GoogleCredentialResponse {
  clientId: string;
  credential: string; // ⭐ chính là ID_TOKEN
  select_by: string;
}

interface GoogleLoginButtonProps {
  rememberMe: boolean;
  setIsGoogleLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  disabled?: boolean;
}

export default function GoogleLoginButton({
  rememberMe,
  setIsGoogleLoading,
  setError,
  disabled = false,
}: GoogleLoginButtonProps) {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleCallback = useCallback(
    async (response: GoogleCredentialResponse) => {
      setIsGoogleLoading(true);
      setError("");

      try {
        const idToken = response.credential; // ⭐ ID_TOKEN (JWT)
        const result = await loginWithGoogle(idToken, rememberMe);

        if (result.success) {
          const from = location.state?.from?.pathname || "/dashboard";
          navigate(from, { replace: true });
        } else {
          setError(result.message);
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Đăng nhập Google thất bại!";
        setError(errorMessage);
      } finally {
        setIsGoogleLoading(false);
      }
    },
    [loginWithGoogle, rememberMe, navigate, location, setIsGoogleLoading, setError]
  );

  useEffect(() => {
    // Load Google script nếu chưa có
    const loadGoogleScript = () => {
      return new Promise<void>((resolve) => {
        if (window.google?.accounts?.id) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => {
          console.error("Failed to load Google Sign-In script");
          resolve();
        };
        document.head.appendChild(script);
      });
    };

    const initializeGoogle = async () => {
      await loadGoogleScript();

      if (!window.google?.accounts?.id) {
        console.error("Google Sign-In script not loaded");
        return;
      }

      if (!buttonRef.current) return;

      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!clientId) {
        console.error("VITE_GOOGLE_CLIENT_ID is not set");
        return;
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCallback,
      });

      window.google.accounts.id.renderButton(buttonRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "signin_with",
        width: "100%",
      });
    };

    initializeGoogle();
  }, [handleCallback]);

  return (
    <div
      ref={buttonRef}
      className="w-full"
      style={{
        minHeight: "36px",
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    />
  );
}
